import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { X, Plus, Paperclip, Eye, Trash2, ArrowLeft } from "lucide-react";
import { logger } from "../services/logService";
import templateService from "../services/templateService";
import s3UploadService, {
  extractBase64Images,
  base64ToFile,
} from "../services/s3UploadService";
import sharedStepService from "../services/sharedStepService";
import sectionService from "../services/sectionService";
import { suiteService } from "../services/suiteService";
import { branchService } from "../services/branchService";
import { projectService } from "../services/projectService";
import { useAuth } from "../context/AuthContext";
import { useModal } from "../context/ModalContext";
import { getStoredClientId } from "../utils/tokenUtils";
import toast from "react-hot-toast";
import { InlineLoadSkeleton } from "./common/InlineLoadSkeleton";
import { Skeleton } from "./common/Skeleton";
import FormSkeleton from "./skeletons/FormSkeleton";

const TestCaseFormModal = ({
  isOpen,
  onClose,
  onSubmit,
  initialData = null,
  projectId,
  defaultSectionId = null,
  suiteId = null,
  viewMode = false,
  onEdit = null,
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { incrementModalCount, decrementModalCount } = useModal();
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    priority: "medium",
    status: "active",
    preconditions: "",
    template_id: "",
    test_case_structure_type: "MULTIPLE_STEPS", // Default structure type
    section_id: null,
    suite_id: null, // For MULTI_SUITE structure
    branch_id: null, // For BASELINE_ENABLED structure
    owner: "",
    reviewer_id: "",
    state: "active",
    type_of_test_case: "other",
    automation_status: "not_automated",
    automation_type: null, // Only for BDD: 'record_and_play' or 'self_automated'
    tags: "",
    attachments: [],
    comments: [], // Array to store comments
    testSteps: [{ step: "", expectedResult: "", gherkin_keyword: "" }],
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [sections, setSections] = useState([]);
  const [loadingSections, setLoadingSections] = useState(false);
  const [sharedSteps, setSharedSteps] = useState([]);
  const [loadingSharedSteps, setLoadingSharedSteps] = useState(false);
  const [showSharedStepsModal, setShowSharedStepsModal] = useState(false);
  const [sharedStepInsertIndex, setSharedStepInsertIndex] = useState(null);
  const [previewAttachment, setPreviewAttachment] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [uploadedStepImages, setUploadedStepImages] = useState(new Set()); // Track images uploaded from steps
  const [initialAttachments, setInitialAttachments] = useState([]); // Track initial attachments for cleanup
  const [projectUsers, setProjectUsers] = useState([]);
  const [loadingProjectUsers, setLoadingProjectUsers] = useState(false);
  const [projectStructure, setProjectStructure] = useState(null); // Project structure (SINGLE_REPOSITORY, MULTI_SUITE, BASELINE_ENABLED)
  const [testCaseTemplateType, setTestCaseTemplateType] =
    useState("MULTIPLE_STEPS"); // SINGLE_STEP, MULTIPLE_STEPS, BDD

  // Root section logic removed - projects start with zero sections
  // Users must explicitly create sections before creating test cases

  // Rich text editor modules
  const quillModules = {
    toolbar: [
      ["bold", "italic", "underline", "strike"],
      ["link", "image", "code-block"],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ table: true }],
      ["clean"],
    ],
  };

  const quillFormats = [
    "bold",
    "italic",
    "underline",
    "strike",
    "link",
    "image",
    "code-block",
    "list",
    "bullet",
    "table",
  ];

  // Load templates for the project
  const loadTemplates = useCallback(async () => {
    try {
      // Pass project_id to filter templates assigned to this project
      // Filter by layout_type='test_case' for test cases
      const filters = { status: "enabled", layout_type: "test_case" };
      if (projectId) {
        filters.project_id = projectId;
      }
      const response = await templateService.getTemplates(filters);

      // Handle different response structures
      const templatesList =
        response?.data?.templates || response?.templates || [];

      // Filter to only test_case layouts (in case backend doesn't filter)
      const testCaseTemplates = templatesList.filter((t) => {
        const layoutType = t.layout_type || "test_case"; // Default to test_case for backward compatibility
        return layoutType === "test_case";
      });

      if (testCaseTemplates.length > 0) {
        // Sort templates: default template first, then others
        const sortedTemplates = [...testCaseTemplates].sort((a, b) => {
          // Put default template first
          if (a.is_default === true && b.is_default !== true) return -1;
          if (a.is_default !== true && b.is_default === true) return 1;
          // If both are default or neither, sort by name (Default first)
          if (a.name === "Default" && b.name !== "Default") return -1;
          if (a.name !== "Default" && b.name === "Default") return 1;
          // If both are system templates, sort by name
          if (a.type === "system" && b.type !== "system") return -1;
          if (a.type !== "system" && b.type === "system") return 1;
          return 0;
        });

        setTemplates(sortedTemplates);

        // Set default template immediately if no template is selected (only for new test cases)
        if (!initialData) {
          const defaultTemplate = sortedTemplates.find((t) => {
            const layoutType = t.layout_type || "test_case";
            return (
              (layoutType === "test_case" && t.is_default === true) ||
              (layoutType === "test_case" &&
                (t.name === "Default" || t.name === "Test Case Steps")) ||
              (layoutType === "test_case" && t.type === "system")
            );
          });

          if (defaultTemplate && defaultTemplate.template_id) {
            // Use setTimeout to ensure state update happens after templates are set
            setTimeout(() => {
              setFormData((prev) => {
                if (!prev.template_id || prev.template_id === "") {
                  return { ...prev, template_id: defaultTemplate.template_id };
                }
                return prev;
              });
            }, 0);
          }
        }
      } else {
        // No templates found - try to initialize default template
        try {
          await templateService.initializeDefaultTemplate?.();
          // Reload templates after initialization
          const retryFilters = { status: "enabled", layout_type: "test_case" };
          if (projectId) {
            retryFilters.project_id = projectId;
          }
          const retryResponse =
            await templateService.getTemplates(retryFilters);
          const retryTemplates =
            retryResponse?.data?.templates || retryResponse?.templates || [];
          // Filter to only test_case layouts
          const testCaseRetryTemplates = retryTemplates.filter((t) => {
            const layoutType = t.layout_type || "test_case";
            return layoutType === "test_case";
          });
          if (testCaseRetryTemplates.length > 0) {
            setTemplates(testCaseRetryTemplates);
            const defaultTemplate = testCaseRetryTemplates.find((t) => {
              const layoutType = t.layout_type || "test_case";
              return (
                (layoutType === "test_case" && t.is_default === true) ||
                (layoutType === "test_case" &&
                  (t.name === "Default" || t.name === "Test Case Steps")) ||
                (layoutType === "test_case" && t.type === "system")
              );
            });
            if (defaultTemplate && !initialData) {
              setFormData((prev) => ({
                ...prev,
                template_id: defaultTemplate.template_id,
              }));
            }
          }
        } catch (initError) {
          console.error("Error initializing default template:", initError);
        }
      }
    } catch (error) {
      console.error("Error loading templates:", error);
      toast.error("Failed to load templates");
    }
  }, [projectId, initialData]);

  // Load sections for the project
  const loadSections = useCallback(async () => {
    if (!projectId) return;

    try {
      setLoadingSections(true);
      const response = await sectionService.getSectionTree(projectId);
      const sectionTree = response?.data?.sections || response?.sections || [];
      // Only set sections if they exist - no root section logic
      setSections(sectionTree);

      // If defaultSectionId is provided, use it; otherwise leave section_id as null
      // No automatic root section selection - user must explicitly select a section
      if (defaultSectionId && sectionTree.length > 0) {
        // Verify the defaultSectionId exists in the tree
        const findSectionById = (sectionList, targetId) => {
          for (const section of sectionList) {
            if (!section) continue;
            if (section.section_id === targetId) {
              return true;
            }
            if (section.children && section.children.length > 0) {
              if (findSectionById(section.children, targetId)) {
                return true;
              }
            }
          }
          return false;
        };

        if (findSectionById(sectionTree, defaultSectionId)) {
          setFormData((prev) => ({
            ...prev,
            section_id: defaultSectionId,
          }));
        }
      }
    } catch (error) {
      logger.error(error, "Failed to load sections");
      setSections([]);
    } finally {
      setLoadingSections(false);
    }
  }, [projectId, defaultSectionId]);

  // Flatten section tree for dropdown display - only show existing sections from API
  const flattenSections = useCallback((sectionList, level = 0) => {
    if (!Array.isArray(sectionList) || sectionList.length === 0) {
      return [];
    }

    let result = [];

    // Only iterate through actual sections from the API - no root section logic
    sectionList.forEach((section) => {
      if (!section || !section.section_id) {
        return;
      }

      const indent = "  ".repeat(level);
      const displayName = section.display_name || section.name;

      // Removed emoji icons - native select doesn't support React components
      // Icons are handled by the section tree component, not in dropdowns

      result.push({
        section_id: section.section_id,
        name: `${indent}${displayName}`, // Removed emoji icons
        level,
        parent_section_id: section.parent_section_id,
      });

      // Recursively add children
      if (
        section.children &&
        Array.isArray(section.children) &&
        section.children.length > 0
      ) {
        result = result.concat(flattenSections(section.children, level + 1));
      }
    });

    return result;
  }, []);

  // Load shared steps for the project
  const loadSharedSteps = useCallback(async () => {
    if (!projectId) return;

    try {
      setLoadingSharedSteps(true);
      const response = await sharedStepService.getSharedSteps(projectId);
      const steps =
        response?.data?.shared_steps || response?.shared_steps || [];
      setSharedSteps(steps);
    } catch (error) {
      logger.error(error, "Failed to load shared steps");
      setSharedSteps([]);
    } finally {
      setLoadingSharedSteps(false);
    }
  }, [projectId]);

  // Load project structure and default template type
  const loadProjectStructure = useCallback(async () => {
    if (!projectId) return;

    try {
      // Get project from projects list - we'll need to filter by project_id
      const clientId = getStoredClientId();
      const response = await projectService.getProjects({
        client_id: clientId,
      });
      const projects = response?.data?.projects || response?.projects || [];
      const project = projects.find((p) => p.project_id === projectId);
      const structure = project?.structure || "SINGLE_REPOSITORY";
      setProjectStructure(structure);

      // Auto-populate structure type from project default (only for new test cases)
      const projectStructureType =
        project?.test_case_structure_type || "MULTIPLE_STEPS";
      setTestCaseTemplateType(projectStructureType);

      if (!initialData && projectStructureType) {
        setFormData((prev) => ({
          ...prev,
          test_case_structure_type: projectStructureType,
          // For SINGLE_STEP, ensure testSteps has exactly one step
          // For BDD, initialize with Gherkin keyword
          testSteps:
            projectStructureType === "SINGLE_STEP"
              ? [{ step: "", expectedResult: "", gherkin_keyword: "" }]
              : projectStructureType === "BDD"
                ? [{ step: "", expectedResult: "", gherkin_keyword: "Given" }]
                : prev.testSteps,
        }));
      }
    } catch (error) {
      logger.error(error, "Failed to load project structure");
      setProjectStructure("SINGLE_REPOSITORY"); // Default fallback
    }
  }, [projectId, initialData]);

  // Load suites for MULTI_SUITE structure
  const loadSuites = useCallback(async () => {
    if (!projectId || projectStructure !== "MULTI_SUITE") {
      return;
    }

    try {
      const clientId = getStoredClientId();
      const response = await suiteService.getSuites(projectId, clientId);
      // Response handling removed as suites state is not used
    } catch (error) {
      logger.error(error, "Failed to load suites");
    }
  }, [projectId, projectStructure]);

  // Load branches for BASELINE_ENABLED structure
  const loadBranches = useCallback(async () => {
    if (
      !projectId ||
      (projectStructure !== "BASELINE_ENABLED" &&
        projectStructure !== "SINGLE_REPOSITORY_WITH_BASELINE_AND_BRANCH")
    ) {
      return;
    }

    try {
      const clientId = getStoredClientId();
      const response = await branchService.getBranches(
        projectId,
        clientId,
        null,
        { status: "active" },
      );

      // Handle different response structures
      let branchesList = [];

      // Check for structure: { success: true, message: { message: "...", data: [...] }, data: null }
      if (response?.message?.data && Array.isArray(response.message.data)) {
        branchesList = response.message.data;
      }
      // Check for structure: { data: [...] }
      else if (response?.data) {
        if (Array.isArray(response.data)) {
          branchesList = response.data;
        } else if (response.data?.data && Array.isArray(response.data.data)) {
          branchesList = response.data.data;
        } else if (
          response.data?.branches &&
          Array.isArray(response.data.branches)
        ) {
          branchesList = response.data.branches;
        }
      }
      // Check if response is directly an array
      else if (Array.isArray(response)) {
        branchesList = response;
      }

      console.log("TestCaseFormModal - Loaded branches:", branchesList); // Debug log
      console.log("TestCaseFormModal - Branch response:", response); // Debug log
    } catch (error) {
      logger.error(error, "Failed to load branches");
      console.error("Error loading branches:", error); // Debug log
    }
  }, [projectId, projectStructure]);

  // Load project users for owner and reviewer dropdowns
  const loadProjectUsers = useCallback(async () => {
    if (!projectId) return;

    try {
      setLoadingProjectUsers(true);
      const response = await projectService.getAssignableUsers(projectId);
      const users = response?.data?.users || response?.users || [];
      setProjectUsers(users);
    } catch (error) {
      logger.error(error, "Failed to load assignable users");
      setProjectUsers([]);
    } finally {
      setLoadingProjectUsers(false);
    }
  }, [projectId]);

  // Include current user (e.g. chief admin) in Assign To dropdown if not already in project users list
  const assignableUsersWithCurrent = useMemo(() => {
    const list = projectUsers || [];
    const uid = user?.user_id || user?.id;
    if (!uid) return list;
    const alreadyInList = list.some((u) => (u.user_id || u.id) === uid);
    if (alreadyInList) return list;
    return [
      {
        user_id: uid,
        full_name: user?.full_name || user?.email || "Me",
        email: user?.email || "",
      },
      ...list,
    ];
  }, [user, projectUsers]);

  // Initialize form data when modal opens
  useEffect(() => {
    if (isOpen) {
      // Register modal with context
      incrementModalCount();

      if (initialData) {
        // Ensure testSteps is properly formatted
        let testSteps = [{ step: "", expectedResult: "", gherkin_keyword: "" }];
        if (
          initialData.testSteps &&
          Array.isArray(initialData.testSteps) &&
          initialData.testSteps.length > 0
        ) {
          testSteps = initialData.testSteps.map((step) => ({
            step: step.step || "",
            expectedResult: step.expectedResult || "",
            gherkin_keyword: step.gherkin_keyword || "",
          }));
        }

        // For SINGLE_STEP, ensure only one step
        if (
          initialData.test_case_structure_type === "SINGLE_STEP" &&
          testSteps.length > 0
        ) {
          testSteps = [testSteps[0]];
        }

        console.log("Initializing form with testSteps:", testSteps);

        setFormData({
          title: initialData.title || "",
          description: initialData.description || "",
          priority: initialData.priority || "medium",
          status: initialData.status || "active",
          preconditions: initialData.preconditions || "",
          template_id: initialData.template_id || "",
          test_case_structure_type:
            initialData.test_case_structure_type || "MULTIPLE_STEPS",
          section_id: initialData.section_id || null,
          suite_id: initialData.suite_id || suiteId || null,
          branch_id: initialData.branch_id || null,
          owner: initialData.owner || user?.user_id || "",
          assigned_to: initialData.assigned_to || user?.user_id || "",
          reviewer_id: initialData.reviewer_id || "",
          state: initialData.state || "active",
          type_of_test_case: initialData.type_of_test_case || "other",
          automation_status: initialData.automation_status || "not_automated",
          automation_type: initialData.automation_type || null,
          tags: initialData.tags?.join(", ") || "",
          attachments: initialData.attachments || [],
          testSteps: testSteps,
        });
      } else {
        // For new test cases, start with empty template_id
        // Default template will be set when templates are loaded
        // Default section_id is the current section (defaultSectionId)
        // If defaultSectionId is null (Root), we'll find Root section ID when sections are loaded
        setFormData({
          title: "",
          description: "",
          priority: "medium",
          status: "active",
          preconditions: "",
          template_id: "",
          test_case_structure_type: "MULTIPLE_STEPS", // Default structure type
          section_id: defaultSectionId || null,
          suite_id: suiteId || null,
          branch_id: null,
          owner: user?.user_id || "",
          assigned_to: user?.user_id || "",
          reviewer_id: "",
          state: "active",
          type_of_test_case: "other",
          automation_status: "not_automated",
          tags: "",
          attachments: [],
          testSteps: [{ step: "", expectedResult: "" }],
        });
      }
      setErrors({});

      // Track initial attachments for cleanup
      if (initialData?.attachments) {
        setInitialAttachments([...initialData.attachments]);
      } else {
        setInitialAttachments([]);
      }

      return () => {
        // Unregister modal with context when closing
        decrementModalCount();
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialData, user, incrementModalCount, decrementModalCount]);

  // Additional effect to update testSteps when initialData changes
  useEffect(() => {
    if (isOpen && initialData && initialData.testSteps) {
      let testSteps =
        Array.isArray(initialData.testSteps) && initialData.testSteps.length > 0
          ? initialData.testSteps.map((step) => ({
              step: step.step || "",
              expectedResult: step.expectedResult || "",
            }))
          : [{ step: "", expectedResult: "", gherkin_keyword: "" }];

      // For SINGLE_STEP, ensure only one step
      if (
        initialData.test_case_structure_type === "SINGLE_STEP" &&
        testSteps.length > 0
      ) {
        testSteps = [testSteps[0]];
      }

      console.log("Updating testSteps from initialData:", testSteps);
      setFormData((prev) => ({
        ...prev,
        testSteps: testSteps,
      }));
    }
  }, [initialData, isOpen]);

  // Set default template when templates are loaded (for new test cases)
  useEffect(() => {
    if (
      templates.length > 0 &&
      !initialData &&
      isOpen &&
      !formData.template_id
    ) {
      const defaultTemplate = templates.find((t) => {
        const layoutType = t.layout_type || "test_case";
        return (
          layoutType === "test_case" &&
          (t.is_default === true ||
            t.name === "Default" ||
            t.name === "Test Case Steps" ||
            t.type === "system")
        );
      });

      if (defaultTemplate && defaultTemplate.template_id) {
        setFormData((prev) => ({
          ...prev,
          template_id: defaultTemplate.template_id,
        }));
      }
    }
  }, [templates, initialData, isOpen, formData.template_id]);

  // Set default section when defaultSectionId changes
  useEffect(() => {
    if (defaultSectionId && isOpen) {
      setFormData((prev) => ({
        ...prev,
        section_id: defaultSectionId,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultSectionId, isOpen]);

  // Load project structure first, then load structure-specific data
  useEffect(() => {
    if (isOpen && projectId) {
      loadProjectStructure();
    }
  }, [isOpen, projectId, loadProjectStructure]);

  // Load structure-specific data when structure is known
  useEffect(() => {
    if (isOpen && projectStructure) {
      loadSuites();
      loadBranches();
    }
  }, [isOpen, projectStructure, loadSuites, loadBranches]);

  // Load templates, sections, shared steps, and project users when modal opens
  useEffect(() => {
    if (isOpen) {
      loadTemplates();
      loadSections();
      loadSharedSteps();
      loadProjectUsers();
    }
  }, [
    isOpen,
    projectId,
    loadTemplates,
    loadSections,
    loadSharedSteps,
    loadProjectUsers,
  ]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // If reviewer is selected, automatically set status to 'review'
    if (name === "reviewer_id" && value) {
      setFormData((prev) => ({ ...prev, [name]: value, status: "review" }));
    } else if (name === "reviewer_id" && !value) {
      // If reviewer is removed, keep current status (user may have selected a different one)
      // Only reset to 'active' if status was 'review' (meaning it was auto-set by reviewer)
      setFormData((prev) => {
        const newStatus = prev.status === "review" ? "active" : prev.status;
        return { ...prev, [name]: value, status: newStatus };
      });
    } else if (name === "status") {
      // Allow user to manually change status, but if reviewer is assigned, force 'review'
      const reviewerId = name === "status" ? formData.reviewer_id : value;
      if (reviewerId) {
        // Reviewer is assigned, force status to 'review'
        setFormData((prev) => ({ ...prev, status: "review" }));
      } else {
        // No reviewer, allow user's selection
        setFormData((prev) => ({ ...prev, [name]: value }));
      }
    } else {
      // For other fields, update normally
      setFormData((prev) => ({ ...prev, [name]: value }));
    }

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleTestStepChange = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      testSteps: prev.testSteps.map((step, i) =>
        i === index ? { ...step, [field]: value } : step,
      ),
    }));
  };

  const addTestStep = () => {
    // Prevent adding steps for SINGLE_STEP template
    if (testCaseTemplateType === "SINGLE_STEP") {
      toast.error("SINGLE_STEP template type allows only one step");
      return;
    }

    setFormData((prev) => ({
      ...prev,
      testSteps: [
        ...prev.testSteps,
        {
          step: "",
          expectedResult: testCaseTemplateType === "BDD" ? "" : "",
          gherkin_keyword: testCaseTemplateType === "BDD" ? "Given" : "",
        },
      ],
    }));
  };

  const removeTestStep = (index) => {
    // Prevent removing steps for SINGLE_STEP template
    if (testCaseTemplateType === "SINGLE_STEP") {
      toast.error("SINGLE_STEP template type requires exactly one step");
      return;
    }

    // Steps are now optional, so allow removing all steps
    setFormData((prev) => ({
      ...prev,
      testSteps: prev.testSteps.filter((_, i) => i !== index),
    }));
  };

  // Insert a shared step into test steps at the specified index
  const handleInsertSharedStep = (sharedStep, index) => {
    setFormData((prev) => ({
      ...prev,
      testSteps: [
        ...prev.testSteps.slice(0, index),
        {
          step: sharedStep.step_description,
          expectedResult: sharedStep.expected_result || "",
          isSharedStep: true,
          sharedStepId: sharedStep.shared_step_id,
        },
        ...prev.testSteps.slice(index),
      ],
    }));
    setShowSharedStepsModal(false);
    setSharedStepInsertIndex(null);
    toast.success("Shared step inserted successfully!");
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Validate file size (50 MB max)
    const maxSize = 50 * 1024 * 1024; // 50 MB in bytes
    const invalidFiles = files.filter((file) => file.size > maxSize);

    if (invalidFiles.length > 0) {
      toast.error(`Some files exceed 50 MB limit. Max file size: 50 MB`);
      return;
    }

    // Validate total files (10 max)
    if (formData.attachments.length + files.length > 10) {
      toast.error("Maximum 10 files allowed");
      return;
    }

    try {
      setUploading(true);
      // Get client_id and project_id for organizing files in Supabase
      const clientId = getStoredClientId() || user?.client_id;

      // Validate clientId and projectId are available
      if (!clientId) {
        toast.error("Client ID is required for file uploads");
        setUploading(false);
        return;
      }

      console.log("Uploading files with:", {
        clientId,
        projectId,
        fileCount: files.length,
      });

      const uploadedUrls = await s3UploadService.uploadFiles(
        files,
        "test-cases",
        clientId,
        projectId,
      );

      // Check if any URLs are placeholders (upload service not configured)
      const hasPlaceholders = uploadedUrls.some(
        (url) => url && url.startsWith("placeholder://"),
      );

      setFormData((prev) => ({
        ...prev,
        attachments: [
          ...prev.attachments,
          ...uploadedUrls.map((url, index) => ({
            url,
            name: files[index].name,
            size: files[index].size,
            type: files[index].type,
          })),
        ],
      }));

      if (hasPlaceholders) {
        toast.warning(
          "File upload service not configured. Files saved as references only. Please set REACT_APP_S3_URL in environment variables.",
        );
      } else {
        toast.success(`${files.length} file(s) uploaded successfully`);
      }

      // Reset file input
      e.target.value = "";
    } catch (error) {
      console.error("File upload error:", error);
      toast.error(
        "Failed to upload files. Please check your S3 configuration or contact support.",
      );
    } finally {
      setUploading(false);
    }
  };

  const removeAttachment = async (index) => {
    const attachmentToRemove = formData.attachments[index];

    // Delete from Supabase if it exists
    if (attachmentToRemove?.url) {
      try {
        await s3UploadService.deleteFile(attachmentToRemove.url);
        console.log(
          "Deleted attachment from Supabase:",
          attachmentToRemove.url,
        );
      } catch (error) {
        console.error("Failed to delete attachment from Supabase:", error);
        // Continue with removal even if delete fails
      }
    }

    setFormData((prev) => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index),
    }));
  };

  // Process HTML content to extract and upload images
  const processHtmlContent = async (htmlContent) => {
    if (!htmlContent) return htmlContent;

    // Check if content already has Supabase URLs (from existing saved content)
    // If it does, skip processing to avoid re-uploading
    if (
      htmlContent.includes("supabase.co") ||
      htmlContent.includes("storage/v1/object/public")
    ) {
      console.log(
        "Content already contains Supabase URLs, skipping image extraction",
      );
      return htmlContent;
    }

    // Extract base64 images from HTML
    const images = extractBase64Images(htmlContent);
    if (images.length === 0) return htmlContent; // No images to process

    // Get client_id and project_id for organizing files
    const clientId = getStoredClientId() || user?.client_id;
    if (!clientId) {
      console.warn("No clientId available, images will be saved as base64");
      return htmlContent;
    }

    const replacements = [];

    // Upload each image to Supabase
    for (const image of images) {
      try {
        // Convert base64 to File
        const file = base64ToFile(image.data, image.fileName, image.mimeType);

        // Upload to Supabase with proper section structure: client_id/project_id/file
        const uploadedUrl = await s3UploadService.uploadFile(
          file,
          "test-steps",
          clientId,
          projectId,
        );

        // Track uploaded image
        setUploadedStepImages((prev) => new Set([...prev, uploadedUrl]));

        // Create replacement mapping
        replacements.push({
          originalSrc: image.src,
          newUrl: uploadedUrl,
          fullDataUrl: image.fullDataUrl,
          imageData: image.data,
        });

        console.log("Uploaded image from step:", {
          fileName: image.fileName,
          url: uploadedUrl,
        });
      } catch (error) {
        console.error("Failed to upload image from step:", error);
        // Continue processing other images even if one fails
      }
    }

    // Replace base64 URLs with Supabase URLs in img src attributes
    let updatedHtml = htmlContent;
    replacements.forEach(({ originalSrc, newUrl, fullDataUrl }) => {
      if (!fullDataUrl) return;

      // Use simple string replacement instead of regex for large base64 data URLs
      // This avoids "Regular expression too large" errors with large images
      // Replace the full data URL directly using string methods
      if (updatedHtml.includes(fullDataUrl)) {
        updatedHtml = updatedHtml.split(fullDataUrl).join(newUrl);
      }

      // Also handle src="..." and src='...' patterns with string replacement
      // Extract a unique prefix from the data URL to match without using the full base64 string
      const dataUrlPrefix = fullDataUrl.substring(
        0,
        Math.min(100, fullDataUrl.indexOf(",") + 1),
      );
      if (dataUrlPrefix && updatedHtml.includes(dataUrlPrefix)) {
        // Use a more targeted approach: find img tags and replace src attributes
        const imgTagRegex = /<img[^>]+>/gi;
        updatedHtml = updatedHtml.replace(imgTagRegex, (imgTag) => {
          if (imgTag.includes(fullDataUrl) || imgTag.includes(dataUrlPrefix)) {
            // Replace the src attribute with the new URL
            return imgTag.replace(
              /src=["']([^"']*)["']/gi,
              (match, srcValue) => {
                if (
                  srcValue === fullDataUrl ||
                  srcValue.startsWith(dataUrlPrefix)
                ) {
                  return `src="${newUrl}"`;
                }
                return match;
              },
            );
          }
          return imgTag;
        });
      }
    });

    return updatedHtml;
  };

  // Process all test steps to extract and upload images
  const processTestStepsForSubmission = async (testSteps) => {
    const processedSteps = [];

    for (const step of testSteps) {
      const processedStep = { ...step };

      // Process step content
      if (step.step) {
        processedStep.step = await processHtmlContent(step.step);
      }

      // Process expected result content (only for non-BDD)
      if (testCaseTemplateType !== "BDD" && step.expectedResult) {
        processedStep.expectedResult = await processHtmlContent(
          step.expectedResult,
        );
      } else if (testCaseTemplateType === "BDD") {
        // BDD should not have expectedResult field
        processedStep.expectedResult = null;
      }

      // For BDD, prepend Gherkin keyword to step text if not already present
      if (testCaseTemplateType === "BDD") {
        const keyword = step.gherkin_keyword || "Given";
        if (!processedStep.step.trim().startsWith(keyword)) {
          processedStep.step = `${keyword} ${processedStep.step.trim()}`;
        }
      }

      processedSteps.push(processedStep);
    }

    return processedSteps;
  };

  // Cleanup: Delete removed attachments and unused step images
  const cleanupRemovedFiles = async () => {
    const filesToDelete = [];

    // Check for removed attachments
    const currentAttachmentUrls = new Set(
      formData.attachments.map((a) => a.url),
    );
    initialAttachments.forEach((attachment) => {
      if (!currentAttachmentUrls.has(attachment.url)) {
        filesToDelete.push(attachment.url);
      }
    });

    // Delete files
    for (const url of filesToDelete) {
      try {
        await s3UploadService.deleteFile(url);
        console.log("Cleaned up removed file:", url);
      } catch (error) {
        console.error("Failed to cleanup file:", error);
      }
    }

    setInitialAttachments([]); // Clear tracking
  };

  const resetForm = () => {
    // Find default template (test_case layout only)
    const defaultTemplate = templates.find((t) => {
      const layoutType = t.layout_type || "test_case";
      return (
        layoutType === "test_case" &&
        (t.is_default === true ||
          t.name === "Test Case Steps" ||
          t.type === "system")
      );
    });

    // Use defaultSectionId if provided, otherwise null (no automatic root section)
    let resetSectionId = defaultSectionId ?? null;

    setFormData({
      title: "",
      description: "",
      priority: "medium",
      status: "active",
      preconditions: "",
      template_id: defaultTemplate?.template_id || "",
      section_id: resetSectionId,
      suite_id: null,
      branch_id: null,
      owner: user?.user_id || "",
      state: "active",
      type_of_test_case: "other",
      automation_status: "not_automated",
      tags: "",
      attachments: [],
      testSteps:
        testCaseTemplateType === "SINGLE_STEP"
          ? [{ step: "", expectedResult: "", gherkin_keyword: "" }]
          : testCaseTemplateType === "BDD"
            ? [{ step: "", expectedResult: "", gherkin_keyword: "Given" }]
            : [{ step: "", expectedResult: "", gherkin_keyword: "" }],
    });
    setErrors({});
    setInitialAttachments([]); // Clear initial attachments tracking
    setUploadedStepImages(new Set()); // Clear uploaded step images tracking
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate required fields
      const newErrors = {};
      const errorMessages = [];

      if (!formData.title.trim()) {
        newErrors.title = "Title is required";
        errorMessages.push("Title is required");
      }

      // Structure-specific validation
      if (projectStructure === "MULTI_SUITE" && !formData.suite_id) {
        newErrors.suite_id = "Suite is required for MULTI_SUITE structure";
        errorMessages.push("Suite is required for MULTI_SUITE structure");
      }
      // SINGLE REPOSITORY WITH BASELINE SUPPORT - commented out
      // if ((projectStructure === 'BASELINE_ENABLED' || projectStructure === 'SINGLE_REPOSITORY_WITH_BASELINE_AND_BRANCH') && !formData.branch_id) {
      //   newErrors.branch_id = 'Branch is required for BASELINE_ENABLED structure';
      //   errorMessages.push('Branch is required for BASELINE_ENABLED structure');
      // }

      if (!formData.owner) {
        newErrors.owner = "Owner is required";
        errorMessages.push("Owner is required");
      }

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        // Show specific error messages
        errorMessages.forEach((msg) => toast.error(msg));
        setLoading(false);
        return;
      }

      // Prepare tags array
      const tagsArray = formData.tags
        ? formData.tags
            .split(",")
            .map((tag) => tag.trim())
            .filter((tag) => tag.length > 0)
        : [];

      // Prepare section_id - convert empty string to null
      const sectionId =
        formData.section_id === "" ||
        formData.section_id === null ||
        formData.section_id === undefined
          ? null
          : formData.section_id;

      // Prepare template_id - ensure we always send a value (use selected template or default)
      let templateId = formData.template_id;
      if (
        !templateId ||
        templateId === "" ||
        templateId === null ||
        templateId === undefined
      ) {
        // If no template selected, try to find default template (test_case layout only)
        const defaultTemplate = templates.find((t) => {
          const layoutType = t.layout_type || "test_case";
          return (
            layoutType === "test_case" &&
            (t.is_default === true ||
              t.name === "Test Case Steps" ||
              t.type === "system")
          );
        });
        templateId = defaultTemplate?.template_id || null;
      }

      // If reviewer is assigned, force status to 'review'
      const finalStatus = formData.reviewer_id ? "review" : formData.status;

      // Process test steps to extract and upload images from HTML content
      const processedTestSteps = await processTestStepsForSubmission(
        formData.testSteps,
      );

      // Cleanup removed attachments before submitting
      await cleanupRemovedFiles();

      // Submit form data
      await onSubmit({
        projectId,
        title: formData.title,
        description: formData.description,
        preconditions: formData.preconditions,
        expected_result:
          testCaseTemplateType === "BDD"
            ? null
            : processedTestSteps.map((step) => step.expectedResult).join("\n"),
        priority: formData.priority,
        status: finalStatus, // Use finalStatus which is 'review' if reviewer is assigned, otherwise user's selection
        template_id: templateId,
        test_case_structure_type: testCaseTemplateType, // Use project template type (immutable)
        section_id: sectionId,
        suite_id:
          projectStructure === "MULTI_SUITE" ||
          projectStructure === "MULTIPLE_TEST_SUITES"
            ? formData.suite_id || suiteId || null
            : null,
        branch_id: null, // SINGLE REPOSITORY WITH BASELINE SUPPORT commented out: was (projectStructure === 'BASELINE_ENABLED' || ...) ? (formData.branch_id || null) : null
        owner: formData.owner,
        assigned_to: formData.assigned_to === "" ? null : formData.assigned_to,
        reviewer_id: formData.reviewer_id || null,
        state: formData.state,
        type_of_test_case: formData.type_of_test_case,
        automation_status: formData.automation_status,
        automation_type: formData.automation_type || null,
        tags: tagsArray,
        attachments: formData.attachments,
        comments: formData.comments || [], // Include comments in submission
        testSteps: processedTestSteps,
        steps: processedTestSteps.map((step, index) => ({
          step_number: index + 1,
          action: step.step,
          expected_result:
            testCaseTemplateType === "BDD" ? null : step.expectedResult || "",
          gherkin_keyword:
            testCaseTemplateType === "BDD"
              ? step.gherkin_keyword || "Given"
              : null,
        })),
      });

      onClose();
    } catch (error) {
      logger.error(error, "Failed to submit test case form");
      toast.error("Failed to create test case");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitAndCreateMore = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate required fields
      const newErrors = {};
      const errorMessages = [];

      if (!formData.title.trim()) {
        newErrors.title = "Title is required";
        errorMessages.push("Title is required");
      }

      // Structure-specific validation
      if (projectStructure === "MULTI_SUITE" && !formData.suite_id) {
        newErrors.suite_id = "Suite is required for MULTI_SUITE structure";
        errorMessages.push("Suite is required for MULTI_SUITE structure");
      }
      // SINGLE REPOSITORY WITH BASELINE SUPPORT - commented out
      // if ((projectStructure === 'BASELINE_ENABLED' || projectStructure === 'SINGLE_REPOSITORY_WITH_BASELINE_AND_BRANCH') && !formData.branch_id) {
      //   newErrors.branch_id = 'Branch is required for BASELINE_ENABLED structure';
      //   errorMessages.push('Branch is required for BASELINE_ENABLED structure');
      // }

      if (!formData.owner) {
        newErrors.owner = "Owner is required";
        errorMessages.push("Owner is required");
      }

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        // Show specific error messages
        errorMessages.forEach((msg) => toast.error(msg));
        setLoading(false);
        return;
      }

      // Prepare tags array
      const tagsArray = formData.tags
        ? formData.tags
            .split(",")
            .map((tag) => tag.trim())
            .filter((tag) => tag.length > 0)
        : [];

      // Prepare section_id - convert empty string to null
      const sectionId =
        formData.section_id === "" ||
        formData.section_id === null ||
        formData.section_id === undefined
          ? null
          : formData.section_id;

      // Prepare template_id - ensure we always send a value (use selected template or default)
      let templateId = formData.template_id;
      if (
        !templateId ||
        templateId === "" ||
        templateId === null ||
        templateId === undefined
      ) {
        // If no template selected, try to find default template (test_case layout only)
        const defaultTemplate = templates.find((t) => {
          const layoutType = t.layout_type || "test_case";
          return (
            layoutType === "test_case" &&
            (t.is_default === true ||
              t.name === "Test Case Steps" ||
              t.type === "system")
          );
        });
        templateId = defaultTemplate?.template_id || null;
      }

      // If reviewer is assigned, force status to 'review'
      const finalStatus = formData.reviewer_id ? "review" : formData.status;

      // Process test steps to extract and upload images from HTML content
      const processedTestSteps = await processTestStepsForSubmission(
        formData.testSteps,
      );

      // Note: Don't cleanup attachments here as we're keeping the form open
      // Attachments will be cleaned up when modal is closed if not saved

      // Submit form data
      await onSubmit({
        projectId,
        title: formData.title,
        description: formData.description,
        preconditions: formData.preconditions,
        expected_result:
          testCaseTemplateType === "BDD"
            ? null
            : processedTestSteps.map((step) => step.expectedResult).join("\n"),
        priority: formData.priority,
        status: finalStatus, // Use finalStatus which is 'review' if reviewer is assigned, otherwise user's selection
        template_id: templateId,
        test_case_structure_type: testCaseTemplateType, // Use project template type (immutable)
        section_id: sectionId,
        suite_id:
          projectStructure === "MULTI_SUITE" ||
          projectStructure === "MULTIPLE_TEST_SUITES"
            ? formData.suite_id || suiteId || null
            : null,
        branch_id: null, // SINGLE REPOSITORY WITH BASELINE SUPPORT commented out: was (projectStructure === 'BASELINE_ENABLED' || ...) ? (formData.branch_id || null) : null
        owner: formData.owner,
        reviewer_id: formData.reviewer_id || null,
        state: formData.state,
        type_of_test_case: formData.type_of_test_case,
        automation_status: formData.automation_status,
        automation_type: formData.automation_type || null,
        tags: tagsArray,
        attachments: formData.attachments,
        comments: formData.comments || [], // Include comments in submission
        testSteps: processedTestSteps,
        steps: processedTestSteps.map((step, index) => ({
          step_number: index + 1,
          action: step.step,
          expected_result:
            testCaseTemplateType === "BDD" ? null : step.expectedResult || "",
          gherkin_keyword:
            testCaseTemplateType === "BDD"
              ? step.gherkin_keyword || "Given"
              : null,
        })),
      });

      // Reset form and show success message
      resetForm();
      setInitialAttachments([]); // Clear initial attachments tracking (already done in resetForm)
      setUploadedStepImages(new Set()); // Clear uploaded step images tracking (already done in resetForm)
      toast.success("Test case created successfully!");
    } catch (error) {
      logger.error(error, "Failed to submit test case form");
      toast.error("Failed to create test case");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50"
      style={{ paddingTop: "80px" }} // Add top padding to avoid nav bar
      onClick={async (e) => {
        if (e.target === e.currentTarget) {
          // Cleanup removed attachments if modal is closed without saving
          if (initialAttachments.length > 0 || uploadedStepImages.size > 0) {
            try {
              // Get attachments that were removed
              const currentAttachmentUrls = new Set(
                formData.attachments.map((a) => a.url),
              );
              const removedAttachments = initialAttachments.filter(
                (a) => !currentAttachmentUrls.has(a.url),
              );

              // Delete removed attachments
              for (const attachment of removedAttachments) {
                try {
                  await s3UploadService.deleteFile(attachment.url);
                  console.log("Cleaned up removed attachment:", attachment.url);
                } catch (error) {
                  console.error("Failed to cleanup attachment:", error);
                }
              }
            } catch (error) {
              console.error("Error during cleanup:", error);
            }
          }

          // Clear tracking state
          setInitialAttachments([]);
          setUploadedStepImages(new Set());

          onClose();
        }
      }}
    >
      <div
        className="bg-bg-primary rounded-lg shadow-xl w-full max-w-[95vw] max-h-[90vh] overflow-hidden relative flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Override browser default disabled styling in view mode */}
        {viewMode && (
          <style>{`
            form select:disabled,
            form input:disabled,
            form textarea:disabled {
              opacity: 1 !important;
              cursor: default !important;
              background-color: var(--color-bg-primary) !important;
              color: var(--color-text-primary) !important;
            }
          `}</style>
        )}

        {/* Header with Back button and Edit/Close buttons */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
          <button
            type="button"
            onClick={async () => {
              // Cleanup removed attachments if modal is closed without saving
              if (
                initialAttachments.length > 0 ||
                uploadedStepImages.size > 0
              ) {
                try {
                  const currentAttachmentUrls = new Set(
                    formData.attachments.map((a) => a.url),
                  );
                  const removedAttachments = initialAttachments.filter(
                    (a) => !currentAttachmentUrls.has(a.url),
                  );

                  for (const attachment of removedAttachments) {
                    try {
                      await s3UploadService.deleteFile(attachment.url);
                    } catch (error) {
                      console.error("Failed to cleanup attachment:", error);
                    }
                  }
                } catch (error) {
                  console.error("Error during cleanup:", error);
                }
              }

              setInitialAttachments([]);
              setUploadedStepImages(new Set());
              onClose();
            }}
            className="flex items-center gap-2 text-indigo-600 hover:text-indigo-700 font-medium"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Test Cases</span>
          </button>

          <div className="flex items-center gap-3">
            {viewMode && onEdit && (
              <button
                type="button"
                onClick={() => {
                  onEdit();
                }}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 text-sm font-medium"
              >
                Edit
              </button>
            )}
            <button
              type="button"
              onClick={async () => {
                if (
                  initialAttachments.length > 0 ||
                  uploadedStepImages.size > 0
                ) {
                  try {
                    const currentAttachmentUrls = new Set(
                      formData.attachments.map((a) => a.url),
                    );
                    const removedAttachments = initialAttachments.filter(
                      (a) => !currentAttachmentUrls.has(a.url),
                    );

                    for (const attachment of removedAttachments) {
                      try {
                        await s3UploadService.deleteFile(attachment.url);
                      } catch (error) {
                        console.error("Failed to cleanup attachment:", error);
                      }
                    }
                  } catch (error) {
                    console.error("Error during cleanup:", error);
                  }
                }

                setInitialAttachments([]);
                setUploadedStepImages(new Set());
                onClose();
              }}
              className="text-text-tertiary hover:text-text-secondary transition-colors"
              aria-label="Close"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Main Content Area - Flex Layout */}
        <form
          onSubmit={
            viewMode
              ? (e) => {
                  e.preventDefault();
                }
              : handleSubmit
          }
          className="flex-1 flex overflow-hidden"
          autoComplete="off"
        >
          {/* Left Column - Main Content */}
          <div className="flex-1 overflow-y-auto p-8">
            <div className="max-w-5xl mx-auto space-y-8">
              {/* Title - Large Input at Top */}
              <div className="mb-8">
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  readOnly={viewMode}
                  placeholder="Enter title for your test case…"
                  className={`w-full text-2xl font-semibold border-0 border-b-2 ${
                    errors.title ? "border-red-500" : "border-gray-200"
                  } focus:border-indigo-600 focus:outline-none py-2 px-0 bg-transparent`}
                />
                {errors.title && (
                  <p className="text-red-500 text-sm mt-1">{errors.title}</p>
                )}
              </div>

              {/* Preconditions */}
              <div className="mb-8">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Preconditions
                </label>
                <div className="bg-white rounded-lg border border-gray-200">
                  <ReactQuill
                    value={formData.preconditions}
                    onChange={(value) =>
                      setFormData((prev) => ({ ...prev, preconditions: value }))
                    }
                    modules={quillModules}
                    formats={quillFormats}
                    readOnly={viewMode}
                    className="min-h-[120px]"
                    placeholder="Add preconditions for this test case..."
                  />
                </div>
              </div>

              {/* Steps */}
              <div className="mb-8">
                <div className="flex items-center justify-between mb-4">
                  <label className="block text-sm font-semibold text-gray-700">
                    Steps
                  </label>
                  {!viewMode && testCaseTemplateType !== "SINGLE_STEP" && (
                    <button
                      type="button"
                      onClick={addTestStep}
                      className="px-3 py-1 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 flex items-center gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      Add Step
                    </button>
                  )}
                </div>

                {testCaseTemplateType === "SINGLE_STEP" ? (
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-4">
                      Step and Expected Result
                    </label>
                    <div className="space-y-4">
                      <div className="border border-gray-200 rounded-lg p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-medium text-text-secondary mb-1">
                              Step <span className="text-red-500">*</span>
                            </label>
                            <div className="border border-border-primary rounded-md">
                              <ReactQuill
                                theme="snow"
                                value={formData.testSteps[0]?.step || ""}
                                onChange={(value) => {
                                  setFormData((prev) => ({
                                    ...prev,
                                    testSteps: [
                                      {
                                        step: value,
                                        expectedResult:
                                          prev.testSteps[0]?.expectedResult ||
                                          "",
                                        gherkin_keyword: "",
                                      },
                                    ],
                                  }));
                                }}
                                readOnly={viewMode}
                                modules={quillModules}
                                formats={quillFormats}
                                placeholder="Enter the test step"
                                style={{ minHeight: "120px" }}
                              />
                            </div>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-text-secondary mb-1">
                              Expected Result{" "}
                              <span className="text-red-500">*</span>
                            </label>
                            <div className="border border-border-primary rounded-md">
                              <ReactQuill
                                theme="snow"
                                value={
                                  formData.testSteps[0]?.expectedResult || ""
                                }
                                onChange={(value) => {
                                  setFormData((prev) => ({
                                    ...prev,
                                    testSteps: [
                                      {
                                        step: prev.testSteps[0]?.step || "",
                                        expectedResult: value,
                                        gherkin_keyword: "",
                                      },
                                    ],
                                  }));
                                }}
                                readOnly={viewMode}
                                modules={quillModules}
                                formats={quillFormats}
                                placeholder="Enter the expected result"
                                style={{ minHeight: "120px" }}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <label className="block text-sm font-medium text-text-primary">
                        {testCaseTemplateType === "BDD"
                          ? "Gherkin Steps"
                          : "Steps and Results"}
                        {testCaseTemplateType === "BDD" && (
                          <span className="ml-2 text-xs text-amber-600">
                            (Use Given/When/Then keywords)
                          </span>
                        )}
                      </label>
                      {!viewMode && testCaseTemplateType !== "SINGLE_STEP" && (
                        <button
                          type="button"
                          onClick={addTestStep}
                          className="px-3 py-1 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 flex items-center gap-2"
                        >
                          <Plus className="w-4 h-4" />
                          Add Step
                        </button>
                      )}
                    </div>

                    <div className="space-y-4">
                      {formData.testSteps.map((testStep, index) => (
                        <div
                          key={index}
                          className="bg-white rounded-lg border border-gray-200 p-4"
                        >
                          <div className="flex items-center justify-between mb-3">
                            <span className="text-sm font-semibold text-gray-700">
                              Step {index + 1}
                            </span>
                            {/* Only show remove button for MULTIPLE_STEPS */}
                            {!viewMode &&
                              testCaseTemplateType === "MULTIPLE_STEPS" && (
                                <div className="flex items-center gap-2">
                                  {formData.testSteps.length > 1 && (
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        removeTestStep(index);
                                      }}
                                      disabled={formData.testSteps.length === 1}
                                      className="p-1 hover:bg-red-100 text-red-600 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                                      title="Remove step"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  )}
                                </div>
                              )}
                          </div>

                          {/* Gherkin Keyword Selector for BDD */}
                          {testCaseTemplateType === "BDD" && !viewMode && (
                            <div className="mb-3">
                              <label className="block text-xs font-medium text-gray-600 mb-1">
                                Gherkin Keyword
                              </label>
                              <select
                                value={testStep.gherkin_keyword || "Given"}
                                onChange={(e) => {
                                  const keyword = e.target.value;
                                  handleTestStepChange(
                                    index,
                                    "gherkin_keyword",
                                    keyword,
                                  );
                                }}
                                disabled={viewMode}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
                                required
                              >
                                <option value="Given">Given</option>
                                <option value="When">When</option>
                                <option value="Then">Then</option>
                                <option value="And">And</option>
                                <option value="But">But</option>
                              </select>
                            </div>
                          )}

                          <div className="mb-3">
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              {testCaseTemplateType === "BDD"
                                ? "Gherkin Step"
                                : "Step Details"}
                            </label>
                            <div className="bg-white rounded-lg border border-gray-200">
                              <ReactQuill
                                value={testStep.step}
                                onChange={(value) =>
                                  handleTestStepChange(index, "step", value)
                                }
                                modules={quillModules}
                                formats={quillFormats}
                                readOnly={viewMode}
                                className="min-h-[100px]"
                                placeholder={
                                  testCaseTemplateType === "BDD"
                                    ? "Given the user is logged in..."
                                    : "Describe the step..."
                                }
                              />
                            </div>
                            {errors[`step_${index}_description`] && (
                              <p className="mt-1 text-xs text-red-600">
                                {errors[`step_${index}_description`]}
                              </p>
                            )}
                          </div>

                          {/* Expected Result - Hidden for BDD, shown for SINGLE_STEP and MULTIPLE_STEPS */}
                          {testCaseTemplateType !== "BDD" && (
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">
                                Expected Result
                              </label>
                              <div className="bg-white rounded-lg border border-gray-200">
                                <ReactQuill
                                  value={testStep.expectedResult}
                                  onChange={(value) =>
                                    handleTestStepChange(
                                      index,
                                      "expectedResult",
                                      value,
                                    )
                                  }
                                  modules={quillModules}
                                  formats={quillFormats}
                                  readOnly={viewMode}
                                  className="min-h-[100px]"
                                  placeholder="What should happen..."
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    {errors.testSteps && (
                      <p className="mt-2 text-sm text-red-600">
                        {errors.testSteps}
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Attachments */}
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Attachments
                </label>
                <div className="border border-border-primary rounded-md p-4">
                  {!viewMode && (
                    <>
                      <input
                        type="file"
                        id="file-upload"
                        multiple
                        onChange={handleFileUpload}
                        disabled={
                          uploading || formData.attachments.length >= 10
                        }
                        className="hidden"
                      />
                      <label
                        htmlFor="file-upload"
                        className={`inline-flex items-center gap-2 px-4 py-2 bg-bg-secondary rounded-md hover:bg-bg-hover cursor-pointer text-sm ${
                          uploading || formData.attachments.length >= 10
                            ? "opacity-50 cursor-not-allowed"
                            : ""
                        }`}
                      >
                        <Paperclip className="w-4 h-4" />
                        {uploading ? "Uploading..." : "Upload Files"}
                      </label>
                    </>
                  )}
                  <p className="text-xs text-text-tertiary mt-2">
                    Max. file size: 50 MB | Max. files: 10 (per upload)
                  </p>

                  {formData.attachments.length > 0 && (
                    <div className="mt-4 space-y-2">
                      {formData.attachments.map((attachment, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between bg-bg-secondary p-2 rounded"
                        >
                          <span
                            className="text-sm text-text-primary truncate flex-1"
                            title={attachment.name || attachment.url}
                          >
                            {attachment.name || attachment.url}
                          </span>
                          <div className="flex items-center gap-2 ml-2">
                            <button
                              type="button"
                              onClick={() => {
                                setPreviewAttachment(attachment);
                                setShowPreviewModal(true);
                              }}
                              className="text-indigo-600 hover:text-indigo-800 p-1 rounded hover:bg-indigo-50 transition-colors"
                              title="Preview attachment"
                            >
                              <Eye className="w-5 h-5" />
                            </button>
                            {!viewMode && (
                              <button
                                type="button"
                                onClick={() => removeAttachment(index)}
                                className="text-red-600 hover:text-red-800 p-1 rounded hover:bg-red-50 transition-colors"
                                title="Delete attachment"
                              >
                                <Trash2 className="w-5 h-5" />
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Footer Actions */}
              {!viewMode && (
                <div className="flex items-center gap-3 pt-6 border-t border-gray-200">
                  <button
                    type="submit"
                    disabled={loading}
                    className={`px-6 py-2.5 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium ${
                      loading ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                  >
                    {loading ? (
                      <span className="flex items-center">
                        <InlineLoadSkeleton className="h-4 w-4 mr-2 rounded-full bg-white/30" />
                        {initialData ? "Updating..." : "Creating..."}
                      </span>
                    ) : initialData ? (
                      "Update"
                    ) : (
                      "Create"
                    )}
                  </button>
                  {!initialData && (
                    <button
                      type="button"
                      onClick={handleSubmitAndCreateMore}
                      disabled={loading}
                      className="px-6 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                    >
                      Add & Create More
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={async () => {
                      if (
                        initialAttachments.length > 0 ||
                        uploadedStepImages.size > 0
                      ) {
                        try {
                          const currentAttachmentUrls = new Set(
                            formData.attachments.map((a) => a.url),
                          );
                          const removedAttachments = initialAttachments.filter(
                            (a) => !currentAttachmentUrls.has(a.url),
                          );

                          for (const attachment of removedAttachments) {
                            try {
                              await s3UploadService.deleteFile(attachment.url);
                            } catch (error) {
                              console.error(
                                "Failed to cleanup attachment:",
                                error,
                              );
                            }
                          }
                        } catch (error) {
                          console.error("Error during cleanup:", error);
                        }
                      }

                      setInitialAttachments([]);
                      setUploadedStepImages(new Set());
                      onClose();
                    }}
                    disabled={loading}
                    className="px-6 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Right Sidebar - Test Case Details */}
          <div className="w-80 bg-gray-100 border-l border-gray-200 overflow-y-auto p-6 flex-shrink-0">
            <h3 className="text-lg font-semibold text-gray-900 mb-6">
              Test Case Details
            </h3>

            <div className="space-y-5">
              {/* Section */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Section <span className="text-red-500">*</span>
                </label>
                <select
                  name="section_id"
                  value={formData.section_id || ""}
                  onChange={handleInputChange}
                  disabled={loadingSections || viewMode}
                  autoComplete="off"
                  className={`w-full px-3 py-2 border ${
                    errors.section_id ? "border-red-500" : "border-gray-300"
                  } rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white`}
                  style={{
                    opacity: viewMode ? 1 : undefined,
                    cursor: viewMode ? "default" : undefined,
                  }}
                >
                  {loadingSections ? (
                    <option value="" disabled>
                      —
                    </option>
                  ) : sections.length === 0 ? (
                    <option value="">No sections available</option>
                  ) : (
                    <>
                      <option value="">Select section</option>
                      {flattenSections(sections).map((section) => (
                        <option
                          key={section.section_id}
                          value={section.section_id}
                        >
                          {section.name}
                        </option>
                      ))}
                    </>
                  )}
                </select>
                {errors.section_id && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.section_id}
                  </p>
                )}
              </div>

              {/* Approver (Reviewer) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Approver
                </label>
                <select
                  name="reviewer_id"
                  value={formData.reviewer_id}
                  onChange={handleInputChange}
                  disabled={viewMode || loadingProjectUsers}
                  autoComplete="off"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
                  style={{
                    opacity: viewMode ? 1 : loadingProjectUsers ? undefined : 1,
                    cursor: viewMode
                      ? "default"
                      : loadingProjectUsers
                        ? "not-allowed"
                        : undefined,
                  }}
                >
                  <option value="">No approver</option>
                  {projectUsers.map((projectUser) => (
                    <option
                      key={projectUser.user_id}
                      value={projectUser.user_id}
                    >
                      {projectUser.full_name || projectUser.email}
                    </option>
                  ))}
                </select>
              </div>

              {/* Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Type
                </label>
                <select
                  name="type_of_test_case"
                  value={formData.type_of_test_case}
                  onChange={handleInputChange}
                  disabled={viewMode}
                  autoComplete="off"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
                  style={{
                    opacity: viewMode ? 1 : undefined,
                    cursor: viewMode ? "default" : undefined,
                  }}
                >
                  <option value="other">Other</option>
                  <option value="functional">Functional</option>
                  <option value="regression">Regression</option>
                  <option value="smoke">Smoke</option>
                  <option value="sanity">Sanity</option>
                  <option value="integration">Integration</option>
                </select>
              </div>

              {/* Priority */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Priority
                </label>
                <select
                  name="priority"
                  value={formData.priority}
                  onChange={handleInputChange}
                  disabled={viewMode}
                  autoComplete="off"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
                  style={{
                    opacity: viewMode ? 1 : undefined,
                    cursor: viewMode ? "default" : undefined,
                  }}
                >
                  <option value="critical">Critical</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              </div>

              {/* Assign To (tag for filtering) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Assign To
                </label>
                <select
                  name="assigned_to"
                  value={formData.assigned_to || ""}
                  onChange={handleInputChange}
                  disabled={viewMode || loadingProjectUsers}
                  autoComplete="off"
                  className={`w-full px-3 py-2 border ${
                    errors.owner ? "border-red-500" : "border-gray-300"
                  } rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white`}
                  style={{
                    opacity: viewMode ? 1 : loadingProjectUsers ? undefined : 1,
                    cursor: viewMode
                      ? "default"
                      : loadingProjectUsers
                        ? "not-allowed"
                        : undefined,
                  }}
                >
                  <option value="">Unassigned</option>
                  {assignableUsersWithCurrent.map((projectUser) => (
                    <option
                      key={projectUser.user_id || projectUser.id}
                      value={projectUser.user_id || projectUser.id}
                    >
                      {projectUser.full_name || projectUser.email}
                    </option>
                  ))}
                </select>
                {errors.owner && (
                  <p className="text-red-500 text-xs mt-1">{errors.owner}</p>
                )}
              </div>

              {/* References (Tags) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  References
                </label>
                <input
                  type="text"
                  name="tags"
                  value={formData.tags}
                  readOnly={viewMode}
                  onChange={handleInputChange}
                  autoComplete="off"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
                  placeholder="Comma-separated tags"
                />
              </div>

              {/* Automation Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Automation Status
                </label>
                <select
                  name="automation_status"
                  value={formData.automation_status}
                  onChange={handleInputChange}
                  disabled={viewMode}
                  autoComplete="off"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
                  style={{
                    opacity: viewMode ? 1 : undefined,
                    cursor: viewMode ? "default" : undefined,
                  }}
                >
                  <option value="not_automated">Not Automated</option>
                  <option value="automated">Automated</option>
                  <option value="not_required">Automation Not Required</option>
                </select>
              </div>

              {/* Automation Type - Only visible for BDD */}
              {testCaseTemplateType === "BDD" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Automation Type
                  </label>
                  <select
                    name="automation_type"
                    value={formData.automation_type || ""}
                    onChange={handleInputChange}
                    disabled={viewMode}
                    autoComplete="off"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
                    style={{
                      opacity: viewMode ? 1 : undefined,
                      cursor: viewMode ? "default" : undefined,
                    }}
                  >
                    <option value="">Select automation type</option>
                    <option value="record_and_play">Record and Play</option>
                    <option value="self_automated">Self Automated</option>
                  </select>
                </div>
              )}
            </div>
          </div>
        </form>
      </div>

      {/* Shared Steps Selection Modal */}
      {showSharedStepsModal && (
        <SharedStepsSelectionModal
          isOpen={showSharedStepsModal}
          onClose={() => {
            setShowSharedStepsModal(false);
            setSharedStepInsertIndex(null);
          }}
          sharedSteps={sharedSteps}
          loading={loadingSharedSteps}
          onSelect={(sharedStep) => {
            if (sharedStepInsertIndex !== null) {
              handleInsertSharedStep(sharedStep, sharedStepInsertIndex);
            }
          }}
        />
      )}

      {/* Attachment Preview Modal */}
      {showPreviewModal && previewAttachment && (
        <AttachmentPreviewModal
          isOpen={showPreviewModal}
          onClose={() => {
            setShowPreviewModal(false);
            setPreviewAttachment(null);
          }}
          attachment={previewAttachment}
        />
      )}
    </div>
  );
};

// Shared Steps Selection Modal Component
const SharedStepsSelectionModal = ({
  isOpen,
  onClose,
  sharedSteps,
  loading,
  onSelect,
}) => {
  const [searchTerm, setSearchTerm] = useState("");

  if (!isOpen) return null;

  const filteredSteps = sharedSteps.filter((step) => {
    const description = step.step_description || "";
    const expectedResult = step.expected_result || "";
    const searchLower = searchTerm.toLowerCase();
    return (
      description.toLowerCase().includes(searchLower) ||
      expectedResult.toLowerCase().includes(searchLower)
    );
  });

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50"
      onClick={onClose}
      style={{ zIndex: 9999 }}
    >
      <div
        className="bg-white rounded-lg shadow-xl p-6 w-full max-w-3xl max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold">Select Shared Step</h2>
          <button
            onClick={onClose}
            className="text-text-tertiary hover:text-text-secondary"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Search */}
        <div className="mb-4">
          <input
            type="text"
            placeholder="Search shared steps..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            autoComplete="off"
            className="w-full px-4 py-2 bg-bg-primary text-text-primary border border-border-primary rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 placeholder:text-text-tertiary"
          />
        </div>

        {/* Shared Steps List */}
        {loading ? (
          <div className="py-6 px-2">
            <FormSkeleton fields={5} className="max-w-none mx-0 p-0" />
          </div>
        ) : filteredSteps.length === 0 ? (
          <div className="text-center py-12 text-text-tertiary">
            {searchTerm
              ? "No shared steps found matching your search."
              : "No shared steps available. Create one first."}
          </div>
        ) : (
          <div className="space-y-3">
            {filteredSteps.map((step) => (
              <div
                key={step.shared_step_id}
                className="border border-border-primary rounded-lg p-4 hover:bg-bg-hover cursor-pointer transition-colors"
                onClick={() => onSelect(step)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div
                      className="text-sm font-medium text-text-primary mb-2"
                      dangerouslySetInnerHTML={{
                        __html:
                          step.step_description?.substring(0, 200) +
                            (step.step_description?.length > 200
                              ? "..."
                              : "") || "No description",
                      }}
                    />
                    {step.expected_result && (
                      <div
                        className="text-xs text-text-secondary"
                        dangerouslySetInnerHTML={{
                          __html:
                            step.expected_result?.substring(0, 150) +
                            (step.expected_result?.length > 150 ? "..." : ""),
                        }}
                      />
                    )}
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelect(step);
                    }}
                    className="ml-4 px-3 py-1 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                  >
                    Insert
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Attachment Preview Modal Component
const AttachmentPreviewModal = ({ isOpen, onClose, attachment }) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);

  useEffect(() => {
    if (isOpen && attachment) {
      setImageError(false);
      setImageLoading(true);
    }
  }, [isOpen, attachment]);

  if (!isOpen || !attachment) return null;

  const fileUrl = attachment.url;
  const fileName = attachment.name || attachment.url;
  const fileType = attachment.type || "";

  // Determine file type for preview
  const isImage =
    fileType.startsWith("image/") ||
    /\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i.test(fileName);
  const isPdf = fileType === "application/pdf" || /\.pdf$/i.test(fileName);
  const isVideo =
    fileType.startsWith("video/") ||
    /\.(mp4|webm|ogg|mov|avi)$/i.test(fileName);

  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = fileUrl;
    link.download = fileName;
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-bg-primary rounded-lg shadow-xl max-w-5xl max-h-[90vh] w-full mx-4 overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <Paperclip className="w-5 h-5 text-text-tertiary flex-shrink-0" />
            <h3
              className="text-lg font-semibold text-text-primary truncate"
              title={fileName}
            >
              {fileName}
            </h3>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleDownload}
              className="p-2 text-text-secondary hover:text-text-primary hover:bg-bg-hover rounded transition-colors"
              title="Download"
            >
              <Paperclip className="w-5 h-5" />
            </button>
            <button
              onClick={onClose}
              className="p-2 text-text-tertiary hover:text-text-secondary rounded transition-colors"
              title="Close"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Preview Content */}
        <div className="flex-1 overflow-auto p-4 bg-bg-secondary">
          {isImage ? (
            <div className="flex items-center justify-center h-full min-h-[400px]">
              {imageLoading && !imageError && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <Skeleton className="h-12 w-12 rounded-full" />
                </div>
              )}
              {imageError ? (
                <div className="text-center text-text-tertiary p-8">
                  <Paperclip className="w-16 h-16 text-text-tertiary mx-auto mb-4" />
                  <p className="mb-2 text-lg text-text-primary">
                    Unable to preview image
                  </p>
                  <p className="mb-4 text-sm text-text-tertiary">
                    The image may be protected or the URL is invalid
                  </p>
                  <div className="flex gap-3 justify-center">
                    <a
                      href={fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
                    >
                      Open in New Tab
                    </a>
                    <button
                      onClick={() => {
                        const link = document.createElement("a");
                        link.href = fileUrl;
                        link.download = fileName;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                      }}
                      className="px-4 py-2 bg-bg-secondary text-text-primary rounded-md hover:bg-bg-hover transition-colors"
                    >
                      Download File
                    </button>
                  </div>
                </div>
              ) : (
                <img
                  src={fileUrl}
                  alt={fileName}
                  className={`max-w-full max-h-full object-contain rounded ${imageLoading ? "opacity-0" : "opacity-100"} transition-opacity`}
                  onLoad={() => {
                    console.log("Image loaded successfully:", fileName);
                    setImageLoading(false);
                    setImageError(false);
                  }}
                  onError={(e) => {
                    console.error("Image load error:", fileUrl);
                    setImageLoading(false);
                    setImageError(true);
                  }}
                  style={{
                    maxWidth: "100%",
                    maxHeight: "calc(90vh - 200px)",
                    objectFit: "contain",
                  }}
                />
              )}
            </div>
          ) : isPdf ? (
            <div className="w-full h-full">
              <iframe
                src={fileUrl}
                className="w-full h-[calc(90vh-150px)] border rounded"
                title={fileName}
                onError={() => {
                  console.error("PDF preview failed");
                }}
              />
            </div>
          ) : isVideo ? (
            <div className="flex items-center justify-center h-full">
              <video
                src={fileUrl}
                controls
                className="max-w-full max-h-full rounded"
                onError={(e) => {
                  console.error("Video preview failed");
                }}
              />
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-center p-8">
              <Paperclip className="w-16 h-16 text-text-tertiary mb-4" />
              <p className="text-text-secondary mb-4">
                Preview not available for this file type
              </p>
              <p className="text-sm text-text-tertiary mb-6">{fileName}</p>
              <button
                onClick={handleDownload}
                className="px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
              >
                Download File
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-border-primary bg-bg-secondary flex items-center justify-between">
          <div className="text-sm text-text-secondary">
            {attachment.size && (
              <span>Size: {(attachment.size / 1024 / 1024).toFixed(2)} MB</span>
            )}
          </div>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-bg-tertiary text-text-primary rounded-md hover:bg-bg-hover transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default TestCaseFormModal;
